import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/Card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/Badge'
import { MoreHorizontal, Trash2, UserCog } from 'lucide-react'
import useRemoveUser from '@/services/api/account/useRemoveUser'
import useGetRoles from '@/services/api/account/useGetRoles'
import { toast } from 'sonner'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/Label'
import useUpdateRoles from '@/services/api/auth/useUpdateRoles'
import { useHasFeature } from '@/components/feature-gate'

interface User {
  id: string
  name: string
  email: string
  avatar?: string
  roles: Array<{
    id: string
    name: string
  }>
}

interface UserCardProps {
  user: User
  currentUserId?: string
  selectedTeamId: string
  onUserUpdated: () => void
}

export default function UserCard({
  user,
  currentUserId,
  selectedTeamId,
  onUserUpdated,
}: UserCardProps) {
  const [showRemoveDialog, setShowRemoveDialog] = useState(false)
  const [showRoleDialog, setShowRoleDialog] = useState(false)
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const rolesEnabled = useHasFeature('roles.enabled')

  const { action: updateRoles, status: rolesStatus } = useUpdateRoles()

  const {
    action: removeUser,
    status: { isPending: removeLoading },
  } = useRemoveUser()

  const { data: availableRoles, isLoading: rolesLoading } = useGetRoles({
    teamId: selectedTeamId,
  })

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
  }

  const handleRemoveUser = async () => {
    try {
      await removeUser({ team_id: selectedTeamId, user_id: user.id })
      toast.success('User removed successfully')
      setShowRemoveDialog(false)
      onUserUpdated()
    } catch (error) {
      toast.error('Failed to remove user')
    }
  }

  const handleOpenRoleDialog = () => {
    setSelectedRoles(user.roles.map((role) => role.name))
    setShowRoleDialog(true)
  }

  const handleRoleToggle = (roleName: string) => {
    setSelectedRoles((prev) => {
      if (prev.includes(roleName)) {
        return prev.filter((role) => role !== roleName)
      } else {
        return [...prev, roleName]
      }
    })
  }

  const handleUpdateRoles = async () => {
    try {
      await updateRoles({
        roles: selectedRoles,
        teamId: selectedTeamId,
        userId: user.id,
      })
      toast.success('User roles updated successfully')
      setShowRoleDialog(false)
      onUserUpdated()
    } catch (error) {
      toast.error('Failed to update user roles')
    }
  }

  return (
    <>
      <Card className="w-full p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Avatar>
              <AvatarImage src={user.avatar} />
              <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
            </Avatar>
            <div>
              <p>{user.name}</p>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {user.roles.map((role) => (
              <Badge variant="secondary" key={role.id}>
                {role.name}
              </Badge>
            ))}

            {user.id !== currentUserId && (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {rolesEnabled && (
                    <DropdownMenuItem onClick={handleOpenRoleDialog}>
                      <UserCog className="mr-2 h-4 w-4" />
                      Change Role
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => setShowRemoveDialog(true)}
                    className="text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Remove User
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </Card>

      {/* Role Change Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Roles</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="mb-4 text-muted-foreground">
              Select roles for {user.name}:
            </p>
            {rolesLoading ? (
              <div className="flex items-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                <span>Loading roles...</span>
              </div>
            ) : (
              <div className="space-y-3">
                {availableRoles?.map((roleName: string) => (
                  <div key={roleName} className="flex items-center space-x-2">
                    <Checkbox
                      id={`role-${roleName}`}
                      checked={selectedRoles.includes(roleName)}
                      onCheckedChange={() => handleRoleToggle(roleName)}
                    />

                    <Label
                      htmlFor={`role-${roleName}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {roleName}
                    </Label>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6 flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowRoleDialog(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateRoles} disabled={rolesLoading}>
                Update Roles
              </Button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>

      {/* Remove User Dialog */}
      <Dialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove User</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="mb-4 text-muted-foreground">
              Are you sure you want to remove {user.name} from the space? This
              action cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowRemoveDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRemoveUser}
                disabled={removeLoading}
                variant="destructive"
              >
                {removeLoading ? 'Removing...' : 'Remove User'}
              </Button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  )
}
